import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DatePickerProps {
	onChange: (date: Date | null) => void;
	clearable?: boolean;
	placeholder?: string;
	className?: string;
	defaultValue?: Date | null;
}

const formatDateDisplay = (date: Date | null): string => {
	if (!date) return '';
	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
};

const DatePicker = ({
	onChange,
	clearable = false,
	placeholder = 'DD/MM/AAAA',
	className = '',
	defaultValue = null,
}: DatePickerProps) => {
	const [selectedDate, setSelectedDate] = useState<Date | null>(defaultValue);
	const [tempDate, setTempDate] = useState<string>(
		formatDateDisplay(defaultValue),
	);
	const [showCalendar, setShowCalendar] = useState<boolean>(false);
	const [currentMonth, setCurrentMonth] = useState<number>(
		defaultValue ? defaultValue.getMonth() : new Date().getMonth(),
	);
	const [currentYear, setCurrentYear] = useState<number>(
		defaultValue ? defaultValue.getFullYear() : new Date().getFullYear(),
	);
	const calendarRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLDivElement>(null);
	const isInitializedRef = useRef<boolean>(false);

	useEffect(() => {
		if (defaultValue && !isInitializedRef.current) {
			const newDate = new Date(defaultValue);
			newDate.setHours(0, 0, 0, 0);
			setSelectedDate(newDate);
			setTempDate(formatDateDisplay(newDate));
			setCurrentMonth(newDate.getMonth());
			setCurrentYear(newDate.getFullYear());
			isInitializedRef.current = true;
		}
	}, [defaultValue]);

	const parseDateInput = (dateString: string): Date | null => {
		const parts = dateString.split('/');
		if (parts.length === 3) {
			const day = parseInt(parts[0]);
			const month = parseInt(parts[1]) - 1;
			const year = parseInt(parts[2]);
			const date = new Date(year, month, day);
			if (!isNaN(date.getTime())) {
				return date;
			}
		}
		return null;
	};

	const clearDate = () => {
		setSelectedDate(null);
		setTempDate('');
		onChange(null);
	};

	const handleDateSelect = (date: Date) => {
		const newDate = new Date(date);
		newDate.setHours(0, 0, 0, 0);

		setSelectedDate(newDate);
		setTempDate(formatDateDisplay(newDate));
		setShowCalendar(false);

		setTimeout(() => {
			onChange(newDate);
		}, 0);
	};

	const handleInputChange = (value: string) => {
		setTempDate(value);
		const date = parseDateInput(value);

		if (date) {
			const newDate = new Date(date);
			newDate.setHours(0, 0, 0, 0);
			setSelectedDate(newDate);
			setCurrentMonth(newDate.getMonth());
			setCurrentYear(newDate.getFullYear());
			onChange(newDate);
		} else {
			setSelectedDate(null);
			onChange(null);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				calendarRef.current &&
				!calendarRef.current.contains(event.target as Node) &&
				inputRef.current &&
				!inputRef.current.contains(event.target as Node)
			) {
				setShowCalendar(false);
			}
		};

		if (showCalendar) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showCalendar]);

	const generateDays = () => {
		const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
		const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
		const daysInMonth = lastDayOfMonth.getDate();
		const startingDayOfWeek = firstDayOfMonth.getDay();

		const days = [];

		for (let i = 0; i < startingDayOfWeek; i++) {
			days.push(<div key={`empty-${i}`} className="h-10 w-10"></div>);
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		for (let day = 1; day <= daysInMonth; day++) {
			const date = new Date(currentYear, currentMonth, day);
			const isToday = date.toDateString() === today.toDateString();
			const isSelected =
				selectedDate &&
				date.toDateString() === selectedDate.toDateString();

			let className =
				'md:size-6 size-5 flex items-center justify-center self-center justify-self-center rounded-full cursor-pointer text-sm text-zinc-600 hover:bg-gray-100';
			if (isToday) className += ' border border-cyan-800';
			if (isSelected)
				className += ' bg-teal-600 !text-white hover:bg-teal-700';

			days.push(
				<div
					key={`day-${day}`}
					className={className}
					onClick={() => handleDateSelect(date)}
				>
					{day}
				</div>,
			);
		}

		return days;
	};

	const changeMonth = (increment: number) => {
		let newMonth = currentMonth + increment;
		let newYear = currentYear;

		if (newMonth < 0) {
			newMonth = 11;
			newYear--;
		} else if (newMonth > 11) {
			newMonth = 0;
			newYear++;
		}

		setCurrentMonth(newMonth);
		setCurrentYear(newYear);
	};

	const monthNames = [
		'Janeiro',
		'Fevereiro',
		'Março',
		'Abril',
		'Maio',
		'Junho',
		'Julho',
		'Agosto',
		'Setembro',
		'Outubro',
		'Novembro',
		'Dezembro',
	];

	const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

	return (
		<div className={cn('relative w-full ', className)}>
			<div className="flex w-full items-center gap-1">
				<div className="relative w-full" ref={inputRef}>
					<div className="relative flex items-center justify-between rounded-md border bg-inherit p-2">
						<input
							type="text"
							className="w-24 cursor-default rounded-md bg-inherit text-sm text-zinc-500 placeholder:text-xs focus:outline-none md:w-28"
							value={tempDate}
							onChange={(e) => handleInputChange(e.target.value)}
							onClick={() => setShowCalendar(true)}
							placeholder={placeholder}
						/>
						<Calendar
							className="size-4 cursor-pointer text-zinc-600"
							onClick={() => setShowCalendar(true)}
						/>
					</div>

					{clearable && selectedDate && (
						<button
							onClick={clearDate}
							className="absolute -right-8 top-1/2 -translate-y-1/2 rounded-full p-2 text-zinc-500 hover:bg-gray-100"
							title="Limpar data"
						>
							<X className="size-4" />
						</button>
					)}
				</div>
			</div>

			{showCalendar && (
				<div
					ref={calendarRef}
					className="z-10 mt-1 w-full max-w-md rounded-lg border border-zinc-200 bg-white p-4 md:absolute md:min-w-max md:shadow-lg"
				>
					<div className="mb-4">
						<div className="flex items-center justify-between">
							<button
								type="button"
								onClick={() => changeMonth(-1)}
								className="rounded p-2 hover:bg-gray-100"
							>
								<ChevronLeft className="size-4 text-zinc-600" />
							</button>
							<h3 className="px-4 font-medium text-zinc-600">
								{monthNames[currentMonth]} {currentYear}
							</h3>
							<button
								type="button"
								onClick={() => changeMonth(1)}
								className="rounded p-2 hover:bg-gray-100"
							>
								<ChevronRight className="size-4 text-zinc-600" />
							</button>
						</div>

						<div className="mt-4 grid grid-cols-7 text-center text-sm">
							{dayNames.map((day) => (
								<div
									key={day}
									className="flex items-center justify-center font-medium text-zinc-500"
								>
									{day}
								</div>
							))}
						</div>

						<div className="mt-2 grid grid-cols-7 gap-1">
							{generateDays()}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export { DatePicker };
